import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Separator } from "@fluentui/react/lib/Separator";
import { Stack } from "@fluentui/react/lib/Stack";

import { CustomPanel, CustomPanelSizeType } from "Components/Generic/CustomPanel";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { uiSetUserSettingsOpen } from "Store/Actions";
import { selectIsUserSettingsOpen } from "Store/Selectors/UI";
import { WithConfiguration } from "Components/Generic/Configuration";
import { AutoRefresh } from "./AutoRefresh";
import { CacheData } from "./CacheData/CacheData";
import { Language } from "./Language";
import { Logout } from "./Logout";
import { Sync } from "./Sync";
import { SystemInformation } from "./SystemInformation/SystemInformation";
import { Theme } from "./Theme";
import { ToolbarIconsOnly } from "./ToolbarIconsOnly";
import { Usernames } from "./Usernames/Usernames";
import { useUserSettingsStyles } from "./UseUserSettingsStyles";
import { IUserSettingsProps } from "./IUserSettingsProps";

export const UserSettings = WithTheme(
  WithTranslator(
    WithConfiguration((props: IUserSettingsProps): JSX.Element => {
      const {
        config,
        translator,
        theme: { spacing }
      } = props;
      const dispatch = useDispatch();
      const isUserSettingsOpen = useSelector(selectIsUserSettingsOpen);
      const onDismiss = useCallback(() => dispatch(uiSetUserSettingsOpen(false)), [dispatch]);
      const styles = useUserSettingsStyles();

      if (!isUserSettingsOpen) {
        return null;
      }

      return (
        <CustomPanel
          customPanelSize={CustomPanelSizeType.small}
          isOpen={isUserSettingsOpen}
          className={styles.root}
          headerTitle={translator.get(`settings.title`, `User Settings`)}
          onDismiss={onDismiss}
          isLightDismiss
        >
          <Stack className={styles.container}>
            <Stack grow verticalFill tokens={{ childrenGap: spacing.m }}>
              <Logout />
              <Separator />
              <Theme />
              <Language />
              <Separator />
              <Usernames />
              <Separator />
              <ToolbarIconsOnly />
              <Separator />
              <AutoRefresh />
              <Separator />
              {config.hasProviderSync() && <Sync />}
            </Stack>
            <Stack>
              <Separator />
            </Stack>
            <Stack horizontal className={styles.footer}>
              <SystemInformation />
              <CacheData />
            </Stack>
          </Stack>
        </CustomPanel>
      );
    })
  )
);
